import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { readThrough } from '@/lib/offlineCache';
import { formatValue } from '@/lib/format';
import { friendlyError, isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';

export const CARE_TEAM_TABLE = 'medic_patient_doctors';
export const REFERRALS_TABLE = 'medic_referrals';
export const LAB_RESULTS_TABLE = 'medic_lab_results';
export const DIAGNOSES_TABLE = 'medic_diagnoses';
export const PRESCRIPTIONS_TABLE = 'medic_prescriptions';
export const ALLERGIES_TABLE = 'medic_allergies';
export const MEASUREMENTS_TABLE = 'medic_measurements';

export type RecordState = 'ready' | 'unavailable' | 'denied' | 'error';

export interface PatientRecordItem {
    id: string;
    title: string;
    subtitle: string;
    date: string;
    statusCode: string;
    statusLabel: string;
    /** Код роли в команде (attending/consultant/…) — для сводки карточки. */
    roleCode?: string;
}

export interface PatientRecordCollection {
    state: RecordState;
    items: PatientRecordItem[];
    error: string;
    note: string;
}

export interface PatientRecordsData {
    careTeam: PatientRecordCollection;
    referrals: PatientRecordCollection;
    labs: PatientRecordCollection;
    diagnoses: PatientRecordCollection;
    prescriptions: PatientRecordCollection;
    allergies: PatientRecordCollection;
    measurements: PatientRecordCollection;
}

type Row = Record<string, string> & { id: string };
type Labels = Record<string, string>;

function slug(): string {
    return useCenterStore.getState().baseSlug || 'local';
}

function cellsOf(row: TableDataRow): Record<string, string> {
    const out: Record<string, string> = {};
    for (const value of row.values ?? []) {
        out[String(value.column)] = value.value === null || value.value === undefined
            ? ''
            : String(value.value);
    }
    return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
    return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function dateText(value: string): string {
    return formatValue(value, 'date:dd.MM.yyyy HH:mm', 'timestamp') || value;
}

function dayText(value: string): string {
    return formatValue(value, '', 'date') || value;
}

function label(labels: Labels, code: string): string {
    return labels[String(code || '')] || code;
}

// Человеческие подписи, если перечисление ещё не загрузилось/недоступно:
// врач не должен видеть технические коды (attending, published и т. п.).
const ENUM_FALLBACKS: Record<string, Labels> = {
    medic_doctor_role: {
        attending: 'Лечащий',
        consultant: 'Консультант',
        referring: 'Направивший',
        gp: 'Терапевт',
    },
    medic_doctor_link_status: { active: 'Активна', ended: 'Завершена' },
    medic_referral_type: {
        lab: 'Лаборатория',
        imaging: 'Инструментальное исследование',
        consult: 'Консультация',
        ecg: 'ЭКГ',
        other: 'Другое',
    },
    medic_referral_status: {
        issued: 'Выдано',
        accepted: 'Принято',
        completed: 'Выполнено',
        cancelled: 'Отменено',
    },
    medic_lab_result_status: {
        draft: 'Черновик',
        preliminary: 'Предварительный',
        final: 'Окончательный',
        amended: 'Изменён',
        cancelled: 'Отменён',
    },
    medic_lab_result_source: {
        manual: 'Введено вручную',
        csv: 'Импорт из файла',
        pdf: 'PDF',
        hl7: 'HL7',
        fhir: 'FHIR',
    },
    medic_diagnosis_status: { active: 'Активный', resolved: 'Снят', ruled_out: 'Исключён' },
    medic_prescription_status: { active: 'Активное', stopped: 'Остановлено', completed: 'Завершено' },
    medic_allergy_severity: { mild: 'Лёгкая', moderate: 'Средняя', severe: 'Тяжёлая' },
    medic_allergy_status: { active: 'Активна', resolved: 'Снята' },
    medic_measurement_type: {
        temperature: 'Температура',
        blood_pressure: 'Артериальное давление',
        pulse: 'Пульс',
        spo2: 'Сатурация',
        weight: 'Вес',
        height: 'Рост',
        glucose: 'Глюкоза',
        other: 'Другое',
    },
    medic_measurement_source: { manual: 'Вручную', device: 'Прибор', import: 'Импорт' },
};

async function enumLabels(code: string): Promise<Labels> {
    const out: Labels = { ...(ENUM_FALLBACKS[code] || {}) };
    try {
        const response = await getSDK().metadata.getEnum({ code });
        for (const value of response.values || []) {
            out[String(value.value)] = String(value.synonym || value.name || value.value);
        }
        return out;
    } catch (e) {
        if (!isNotFoundError(e)) logSwallowed(e, `Карта пациента: перечисление ${code}`);
        return out;
    }
}

async function loadTable(table: string, patientId: string, pageSize = 500): Promise<Row[]> {
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table,
        pageSize,
        page: 0,
        filters: [{ column: 'patient_id', value: patientId }],
    });
    return rowsOf(response.rows).filter((row) => row.id !== '');
}

function errorCollection(e: unknown): PatientRecordCollection {
    if (isPermissionDeniedError(e)) {
        return { state: 'denied', items: [], error: 'Нет прав на чтение раздела', note: '' };
    }
    if (isNotFoundError(e)) {
        return { state: 'unavailable', items: [], error: '', note: 'Раздел недоступен в этой базе.' };
    }
    logSwallowed(e, 'Карта пациента: раздел записей');
    return {
        state: 'error',
        items: [],
        error: friendlyError(e),
        note: '',
    };
}

async function loadCollection(
    table: string,
    patientId: string,
    map: (rows: Row[], labels: Record<string, Labels>) => PatientRecordItem[],
    labelCodes: Array<[string, string]>,
): Promise<PatientRecordCollection> {
    try {
        const result = await readThrough(
            `patient-card:${table}:v2:${slug()}:${patientId}`,
            async () => {
                const [rows, ...loaded] = await Promise.all([
                    loadTable(table, patientId),
                    ...labelCodes.map(([, code]) => enumLabels(code)),
                ]);
                const labels: Record<string, Labels> = {};
                labelCodes.forEach(([key], index) => {
                    labels[key] = loaded[index] || {};
                });
                return map(rows, labels);
            },
        );
        return { state: 'ready', items: result.data, error: '', note: '' };
    } catch (e) {
        return errorCollection(e);
    }
}

export async function loadPatientRecords(patientId: string): Promise<PatientRecordsData> {
    const [careTeam, referrals, labs, diagnoses, prescriptions, allergies, measurements] = await Promise.all([
        loadCollection(CARE_TEAM_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: row.doctor_id_presentation || 'Врач не указан',
                subtitle: [
                    label(labels.role, row.role),
                    row.reason,
                ].filter(Boolean).join(' · '),
                date: dayText(row.started_at),
                statusCode: row.status,
                statusLabel: label(labels.status, row.status),
                roleCode: row.role,
            }))
            .sort((a, b) => {
                const aActive = String(a.statusCode).toLowerCase() === 'active' ? 0 : 1;
                const bActive = String(b.statusCode).toLowerCase() === 'active' ? 0 : 1;
                if (aActive !== bActive) return aActive - bActive;
                return String(b.date).localeCompare(String(a.date));
            }), [
            ['role', 'medic_doctor_role'],
            ['status', 'medic_doctor_link_status'],
        ]),
        loadCollection(REFERRALS_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: row.target || label(labels.type, row.referral_type) || 'Направление',
                subtitle: [
                    row.target ? label(labels.type, row.referral_type) : '',
                    row.doctor_id_presentation,
                ].filter(Boolean).join(' · '),
                date: dayText(row.date),
                statusCode: row.status,
                statusLabel: label(labels.status, row.status),
            }))
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['type', 'medic_referral_type'],
            ['status', 'medic_referral_status'],
        ]),
        loadCollection(LAB_RESULTS_TABLE, patientId, (rows, labels) => rows
            .map((row) => {
                const comment = String(row.comment || '');
                const visibleComment = /^Импорт CSV:/i.test(comment) ? '' : comment;
                return {
                    id: row.id,
                    title: row.lab_name || 'Результат анализов',
                    subtitle: [label(labels.source, row.source), visibleComment]
                        .filter(Boolean).join(' · '),
                    date: dateText(row.resulted_at || row.collected_at || row.date),
                    statusCode: row.status,
                    statusLabel: label(labels.status, row.status),
                };
            })
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['status', 'medic_lab_result_status'],
            ['source', 'medic_lab_result_source'],
        ]),
        loadCollection(DIAGNOSES_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: row.diagnosis_text || 'Диагноз',
                subtitle: [row.icd10_code, row.doctor_id_presentation]
                    .filter(Boolean).join(' · '),
                date: dayText(row.onset_at),
                statusCode: row.status,
                statusLabel: label(labels.status, row.status),
            }))
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['status', 'medic_diagnosis_status'],
        ]),
        loadCollection(PRESCRIPTIONS_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: [row.drug_name, row.dose].filter(Boolean).join(' · ') || 'Назначение',
                subtitle: [row.frequency, row.route, row.doctor_id_presentation]
                    .filter(Boolean).join(' · '),
                date: dayText(row.start_at),
                statusCode: row.status,
                statusLabel: label(labels.status, row.status),
            }))
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['status', 'medic_prescription_status'],
        ]),
        loadCollection(ALLERGIES_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: row.allergen || 'Аллергия',
                subtitle: [row.reaction, label(labels.severity, row.severity)]
                    .filter(Boolean).join(' · '),
                date: dayText(row.recorded_at),
                statusCode: row.status,
                statusLabel: label(labels.status, row.status),
            }))
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['severity', 'medic_allergy_severity'],
            ['status', 'medic_allergy_status'],
        ]),
        loadCollection(MEASUREMENTS_TABLE, patientId, (rows, labels) => rows
            .map((row) => ({
                id: row.id,
                title: [label(labels.type, row.measurement_type), row.value_num, row.unit]
                    .filter(Boolean).join(' '),
                subtitle: row.doctor_id_presentation || label(labels.source, row.source),
                date: dateText(row.measured_at || row.date),
                statusCode: '',
                statusLabel: '',
            }))
            .sort((a, b) => String(b.date).localeCompare(String(a.date))), [
            ['type', 'medic_measurement_type'],
            ['source', 'medic_measurement_source'],
        ]),
    ]);
    return { careTeam, referrals, labs, diagnoses, prescriptions, allergies, measurements };
}
